/**
 * Snapshot → render inputs (M6.md). The read-only viewer/embed reuses the SAME
 * M4 MapCanvas (`mode="embed"`), which takes a PointSet + MapSpec. A published
 * snapshot stores pre-resolved GeoJSON, so this pure adapter reconstructs the
 * PointSet from it — no re-geocoding, no DB rows, one renderer for editor and
 * viewer. Reserved keys (`_id`, `_confidence`) are stripped back out of props.
 */

import type { InferredType } from "@/lib/ingest";
import type { Point, PointColumn, PointSet } from "@/lib/map/pointset";
import { boundsOf } from "@/lib/map/pointset";
import type { MapSnapshot } from "./snapshot";

const RESERVED = new Set(["_id", "_confidence"]);

function inferType(values: string[]): InferredType {
  const nonEmpty = values.filter((v) => v !== "");
  if (nonEmpty.length > 0 && nonEmpty.every((v) => Number.isFinite(Number(v)))) return "number";
  return "text";
}

/** Rebuild the PointSet the renderer needs from a published snapshot's GeoJSON. */
export function pointSetFromSnapshot(snapshot: MapSnapshot): PointSet {
  const features = snapshot.geojson.features ?? [];
  const points: Point[] = [];
  const columnValues = new Map<string, string[]>();

  for (const f of features) {
    const props = (f as { properties?: Record<string, string | number> }).properties ?? {};
    const coords = (f as { geometry?: { coordinates?: [number, number] } }).geometry?.coordinates;
    if (!coords) continue;
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(props)) {
      if (RESERVED.has(k)) continue;
      const s = String(v ?? "");
      cleaned[k] = s;
      (columnValues.get(k) ?? columnValues.set(k, []).get(k)!).push(s);
    }
    points.push({
      id: String(props._id ?? points.length),
      lng: coords[0],
      lat: coords[1],
      confidence: Number(props._confidence ?? 1),
      props: cleaned,
    });
  }

  const columns: PointColumn[] = [...columnValues.entries()].map(([name, values]) => ({
    name,
    type: inferType(values),
    distinctSample: [...new Set(values)].slice(0, 8),
  }));

  return { columns, points, bounds: boundsOf(points), count: points.length };
}
